import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Snowflake, Moon, ChevronRight, Wind, ArrowUp, ArrowDown, Thermometer, Sun, ShoppingBasket } from 'lucide-react';
import { atmosphereFor, frostLevelFrom } from '@/lib/atmosphere';

const STAR_POS = [
  { top: '14%', left: '10%', s: 3 }, { top: '22%', left: '28%', s: 2 }, { top: '12%', left: '46%', s: 2 },
  { top: '30%', left: '62%', s: 3 }, { top: '18%', left: '78%', s: 2 }, { top: '40%', left: '18%', s: 2 },
  { top: '48%', left: '38%', s: 3 }, { top: '36%', left: '54%', s: 2 }, { top: '52%', left: '70%', s: 2 },
  { top: '26%', left: '88%', s: 3 }, { top: '60%', left: '24%', s: 2 }, { top: '58%', left: '50%', s: 2 },
  { top: '64%', left: '82%', s: 3 }, { top: '44%', left: '92%', s: 2 }, { top: '70%', left: '12%', s: 2 },
  { top: '16%', left: '64%', s: 2 },
];
const RAYS = Array.from({ length: 12 }).map((_, i) => i * 30);

function SunLayer({ dusk }) {
  const core = dusk ? '#FFE0B0' : '#FFD23F';
  const halo = dusk ? 'rgba(245,170,90,0.55)' : 'rgba(255,206,70,0.62)';
  const rayColor = dusk ? 'rgba(245,180,110,0.5)' : 'rgba(255,210,70,0.55)';
  return (
    <div className="absolute" style={{ top: 8, right: 34, width: 110, height: 110 }}>
      <div className="absolute inset-0 flex items-center justify-center" style={{ animation: 'hgSkySpin 26s linear infinite' }}>
        <div className="relative" style={{ width: 150, height: 150 }}>
          {RAYS.map((deg) => (
            <div key={deg} className="absolute left-1/2 top-1/2" style={{
              width: 3, height: 150, marginLeft: -1.5, marginTop: -75, borderRadius: 2,
              background: rayColor, transform: `rotate(${deg}deg)`, transformOrigin: 'center',
            }} />
          ))}
        </div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center" style={{ animation: 'hgSkyPulse 3.4s ease-in-out infinite' }}>
        <div style={{ position: 'absolute', width: 120, height: 120, borderRadius: 999, background: halo, filter: 'blur(6px)' }} />
        <div style={{ width: 64, height: 64, borderRadius: 999, background: core, boxShadow: `0 0 32px 8px ${dusk ? 'rgba(229,140,90,0.8)' : 'rgba(245,184,0,0.85)'}` }} />
      </div>
    </div>
  );
}

function CloudLayer({ dense }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute" style={{ top: 20, right: 44, width: 84, height: 84, borderRadius: 999, background: 'rgba(255,248,228,0.85)', filter: 'blur(2px)', boxShadow: '0 0 30px 6px rgba(253,231,184,0.7)' }} />
      <div className="absolute" style={{ top: 26, left: '6%', width: 160, height: 40, borderRadius: 26, background: 'rgba(255,255,255,0.7)', filter: 'blur(5px)', animation: 'hgCloudDrift 14s ease-in-out infinite' }} />
      <div className="absolute" style={{ top: 62, right: '9%', width: 118, height: 34, borderRadius: 22, background: `rgba(255,255,255,${dense ? 0.5 : 0.34})`, filter: 'blur(5px)', animation: 'hgCloudDrift2 18s ease-in-out infinite' }} />
      <div className="absolute" style={{ top: 104, left: '30%', width: 170, height: 36, borderRadius: 24, background: `rgba(255,255,255,${dense ? 0.44 : 0.26})`, filter: 'blur(6px)', animation: 'hgCloudDrift 22s ease-in-out infinite' }} />
    </div>
  );
}

function RainLayer() {
  const drops = Array.from({ length: 16 }).map((_, i) => ({ left: `${(i * 6 + 3) % 98}%`, delay: ((i * 120) % 900) / 1000, len: 16 + (i % 3) * 9 }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {drops.map((d, i) => (
        <div key={i} className="absolute" style={{
          left: d.left, top: -30, width: 2, height: d.len, borderRadius: 2, background: 'rgba(255,255,255,0.75)',
          animation: 'hgRainDrop 0.85s linear infinite', animationDelay: `${d.delay}s`,
        }} />
      ))}
      {drops.filter((_, i) => i % 3 === 0).map((d, i) => (
        <div key={`s${i}`} className="absolute" style={{
          left: d.left, bottom: 6, width: 18, height: 8, borderRadius: 9, border: '1.5px solid rgba(255,255,255,0.6)',
          animation: 'hgSplash 0.85s ease-out infinite', animationDelay: `${(d.delay + 0.5) % 0.85}s`,
        }} />
      ))}
    </div>
  );
}

function FrostLayer() {
  const flakes = Array.from({ length: 12 }).map((_, i) => ({ left: `${(i * 9 + 4) % 96}%`, delay: ((i * 260) % 3000) / 1000, size: 12 + (i % 3) * 6, dur: 3.2 + (i % 4) * 0.7 }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-x-0 top-0 h-10" style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.85), transparent)' }} />
      <div className="absolute inset-x-0 bottom-0 h-11" style={{ background: 'linear-gradient(to top, rgba(255,255,255,0.6), transparent)' }} />
      <div className="absolute inset-y-0 left-0 w-9" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0.7), transparent)' }} />
      <div className="absolute inset-y-0 right-0 w-9" style={{ background: 'linear-gradient(to left, rgba(255,255,255,0.7), transparent)' }} />
      {flakes.map((f, i) => (
        <div key={i} className="absolute" style={{ left: f.left, top: -24, animation: `hgFlake ${f.dur}s linear infinite`, animationDelay: `${f.delay}s` }}>
          <Snowflake style={{ width: f.size, height: f.size, color: 'rgba(255,255,255,0.92)' }} />
        </div>
      ))}
    </div>
  );
}

function StarLayer() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute" style={{ top: 14, right: 34, width: 54, height: 54, borderRadius: 999, background: 'rgba(210,224,255,0.32)', filter: 'blur(2px)' }} />
      <div className="absolute" style={{ top: 16, right: 38 }}><Moon style={{ width: 44, height: 44, color: '#F2F7FF' }} /></div>
      {STAR_POS.map((st, i) => (
        <div key={i} className="absolute" style={{
          top: st.top, left: st.left, width: st.s, height: st.s, borderRadius: st.s, background: '#FDFBEF',
          opacity: 0.6, animation: i % 3 === 0 ? 'hgTwinkle 1.4s ease-in-out infinite' : 'none',
        }} />
      ))}
    </div>
  );
}

function WindLayer() {
  const streaks = [
    { top: 34, delay: 0, dur: 3.2, width: 96 }, { top: 58, delay: 0.9, dur: 2.6, width: 64 },
    { top: 80, delay: 1.7, dur: 3.8, width: 120 }, { top: 104, delay: 0.5, dur: 2.9, width: 78 },
    { top: 132, delay: 2.2, dur: 3.4, width: 100 },
  ];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {streaks.map((s, i) => (
        <div key={i} className="absolute" style={{
          top: s.top, left: -s.width, width: s.width, height: 2.5, borderRadius: 3, background: 'rgba(150,161,172,0.5)',
          animation: `hgWind ${s.dur}s ease-in-out infinite`, animationDelay: `${s.delay}s`,
        }} />
      ))}
    </div>
  );
}

export function LivingSky({ atmo }) {
  const m = atmo.mood;
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: `linear-gradient(135deg, ${atmo.gradient[0]}, ${atmo.gradient[1]}, ${atmo.gradient[2]})` }}>
      {(m === 'sun' || m === 'dusk') && <SunLayer dusk={m === 'dusk'} />}
      {(m === 'cloud' || m === 'mist') && <CloudLayer dense={m === 'mist'} />}
      {m === 'rain' && <RainLayer />}
      {m === 'thunder' && <><RainLayer /><div className="absolute inset-0" style={{ background: '#FBFAF0', animation: 'hgFlash 5s ease-out infinite' }} /></>}
      {m === 'frost' && <FrostLayer />}
      {m === 'night' && <StarLayer />}
      {atmo.windy && <WindLayer />}
    </div>
  );
}

// ── Harvest Backdrop — full-canvas vegetable-basket photo behind the Today
// screen, with a frosted-glass scrim for legibility and the Living Weather
// animations drifting over the harvest basket. Portalled to <body> so it sits
// behind the content and covers any viewport (iPad → ultra-wide).
export function TodayHarvestBackdrop({ weatherRaw, localHour }) {
  useEffect(() => {
    document.body.classList.add('hg-today-bg');
    return () => document.body.classList.remove('hg-today-bg');
  }, []);

  let atmo = null;
  if (weatherRaw?.current) {
    atmo = atmosphereFor(weatherRaw, localHour ?? 12, frostLevelFrom(weatherRaw));
  }

  const node = (
    <div aria-hidden data-testid="today-harvest-backdrop"
      style={{ position: 'fixed', inset: 0, zIndex: -1, pointerEvents: 'none', overflow: 'hidden' }}>
      {/* 1 — crisp, high-fidelity harvest photo (matches mobile: vibrant, unblurred) */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'url(/harvest-basket.webp)',
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
      }} />
      {/* 2 — Living Weather drifting over the basket (subtle sky accent up top) */}
      {atmo && (
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.4, mixBlendMode: 'soft-light',
          WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.75) 35%, rgba(0,0,0,0.25) 70%, rgba(0,0,0,0) 100%)',
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.75) 35%, rgba(0,0,0,0.25) 70%, rgba(0,0,0,0) 100%)',
        }}>
          <LivingSky atmo={atmo} />
        </div>
      )}
      {/* 3 — light cream veil for legibility (matches mobile 0.28 → 0.44 → 0.6) */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(250,246,236,0.28) 0%, rgba(250,246,236,0.44) 50%, rgba(250,246,236,0.6) 100%)',
      }} />
    </div>
  );

  return createPortal(node, document.body);
}

// ── Today Vitals — 4 frosted-glass chips (Soil / Frost / UV / Next harvest),
// a 1:1 web port of the mobile TodayHud vitals row. Sits directly under the
// weather header so the two read as one HUD unit.
const FROST_TONE = { none: '#4E7A33', low: '#8FB4D6', medium: '#C9A85B', high: '#C66B4A', unknown: '#5C665C' };
const UV_TONE = { low: '#4E7A33', moderate: '#C9A85B', high: '#C66B4A', very_high: '#C66B4A', extreme: '#B23A2E' };

export function TodayVitals({ vitals }) {
  if (!vitals) return null;
  const soil = vitals.soil_temp;
  const frost = vitals.frost_risk;
  const uv = vitals.uv;
  const nh = vitals.next_harvest;

  const frostAlert = frost?.available && (frost.level === 'high' || frost.level === 'medium');
  const uvAlert = uv?.available && ['high', 'very_high', 'extreme'].includes(uv.level);

  const items = [
    { key: 'soil', testID: 'today-vital-soil', Icon: Thermometer, tone: '#1A4331',
      value: soil?.available ? `${soil.value}°` : '—', label: 'Soil', alert: false },
    { key: 'frost', testID: 'today-vital-frost', Icon: Snowflake, tone: FROST_TONE[frost?.level] || '#5C665C',
      value: frost?.available ? (frost.tonight_min != null ? `${frost.tonight_min}°` : frost.label) : '—',
      label: frost?.available ? frost.label : 'Frost', alert: !!frostAlert },
    { key: 'uv', testID: 'today-vital-uv', Icon: Sun, tone: UV_TONE[uv?.level] || '#5C665C',
      value: uv?.available ? `${uv.value}` : '—', label: uv?.available ? `UV ${uv.label}` : 'UV', alert: !!uvAlert },
    { key: 'harvest', testID: 'today-vital-harvest', Icon: ShoppingBasket, tone: '#4E7A33',
      value: nh ? (nh.ready ? 'Now' : `${nh.days_away}d`) : '—', label: nh ? 'Harvest' : 'No crops', alert: false },
  ];

  return (
    <div className="flex gap-2 mt-3" data-testid="today-vitals-row">
      {items.map((v) => (
        <div key={v.key} data-testid={v.testID}
          className="relative flex-1 rounded-2xl px-2 py-2 overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 6px 16px -8px rgba(26,67,49,0.18)' }}>
          {v.alert && (
            <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ border: `1.5px solid ${v.tone}`, animation: 'hgPulse 1.15s ease-in-out infinite' }} />
          )}
          <div className="flex items-center justify-between" style={{ height: 14 }}>
            <v.Icon className="w-[13px] h-[13px]" style={{ color: v.tone }} />
            {v.alert && <span className="rounded-full" style={{ width: 7, height: 7, background: v.tone, animation: 'hgPulse 1.15s ease-in-out infinite' }} />}
          </div>
          <p className="mt-0.5 font-bold leading-none truncate" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 18, color: v.tone }}>{v.value}</p>
          <p className="font-semibold truncate" style={{ fontSize: 10, color: '#1A4331', opacity: 0.8 }}>{v.label}</p>
        </div>
      ))}
    </div>
  );
}

// The Living Atmosphere hero card for the web Today screen.
export function LivingAtmosphereHud({ weatherRaw, localHour }) {
  if (!weatherRaw?.current) return null;
  const frost = frostLevelFrom(weatherRaw);
  const atmo = atmosphereFor(weatherRaw, localHour ?? 12, frost);
  const cur = weatherRaw.current || {};
  const daily = weatherRaw.daily || {};
  const temp = Math.round(cur.temperature_2m ?? 0);
  const hi = Math.round(daily.temperature_2m_max?.[0] ?? 0);
  const lo = Math.round(daily.temperature_2m_min?.[0] ?? 0);

  return (
    <Link to="/weather" data-testid="living-atmosphere-hud"
      className="hg-press block relative rounded-3xl overflow-hidden shadow-[0_18px_50px_-18px_rgba(15,45,31,0.5)] h-[210px] md:h-[230px]">
      <LivingSky atmo={atmo} />
      {/* soft legibility scrim */}
      <div className="absolute inset-0" style={{ background: atmo.dark ? 'linear-gradient(90deg, rgba(0,0,0,0.28), transparent 65%)' : 'linear-gradient(90deg, rgba(255,255,255,0.35), transparent 65%)' }} />
      <div className="relative z-10 h-full flex flex-col justify-between p-6">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: atmo.subTextColor }} data-testid="atmo-caption">
            {atmo.caption}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: atmo.subTextColor }}>
            Full forecast <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
        <div>
          <div className="flex items-end gap-3">
            <span className="text-6xl md:text-7xl font-bold leading-none" style={{ color: atmo.textColor, fontFamily: 'Outfit, sans-serif' }} data-testid="atmo-temp">
              {temp}°
            </span>
            <span className="text-lg font-semibold pb-2" style={{ color: atmo.textColor }} data-testid="atmo-condition">
              {atmo.condition}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm font-medium" style={{ color: atmo.subTextColor }}>
            <span className="inline-flex items-center gap-1"><ArrowUp className="w-4 h-4" />{hi}°</span>
            <span className="inline-flex items-center gap-1"><ArrowDown className="w-4 h-4" />{lo}°</span>
            <span className="inline-flex items-center gap-1"><Wind className="w-4 h-4" />{atmo.windSpeed} km/h</span>
            {frost !== 'none' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ background: 'rgba(143,180,214,0.35)', color: atmo.textColor }} data-testid="atmo-frost">
                <Snowflake className="w-3.5 h-3.5" /> Frost {frost === 'high' ? 'tonight' : 'risk'}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
